import { z } from 'zod';
import { APP_SLUG_PATTERN } from './app-slug';
/**
 * Typed Choice / Score / Boolean questions to a model, mirroring the AI SDK
 * evaluation question shape. Questions and metadata arrive from untrusted app
 * bundles over the worker RPC, so every string and count is bounded.
 */ export const MAX_DECISION_QUESTIONS = 16;
export const MAX_DECISION_CHOICE_OPTIONS = 16;
export const MAX_DECISION_SCORE_LEVELS = 10;
export const MAX_DECISION_TEXT_LENGTH = 2000;
export const MAX_DECISION_IDENTIFIER_LENGTH = 200;
const MAX_DECISION_OPTION_NAME_LENGTH = 100;
const MAX_DECISION_METADATA_ENTRIES = 16;
const MAX_DECISION_METADATA_VALUE_LENGTH = 500;
const MAX_DECISION_THRESHOLD_MODELS = 16;
const DecisionTextSchema = z.string().min(1).max(MAX_DECISION_TEXT_LENGTH);
const IdentifierSchema = z.string().min(1).max(MAX_DECISION_IDENTIFIER_LENGTH);
const SlugSchema = z.string().max(MAX_DECISION_IDENTIFIER_LENGTH).regex(new RegExp(`^${APP_SLUG_PATTERN}$`), 'Must be lowercase alphanumeric with hyphens');
function boundedRecord(key, value, min, max, what) {
    return z.record(key, value).refine((record)=>{
        const count = Object.keys(record).length;
        return count >= min && count <= max;
    }, `${what}: between ${min} and ${max} entries`);
}
export const DecisionQuestionSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('choice'),
        instructions: DecisionTextSchema,
        criteria: boundedRecord(z.string().min(1).max(MAX_DECISION_OPTION_NAME_LENGTH), DecisionTextSchema, 2, MAX_DECISION_CHOICE_OPTIONS, 'choice options')
    }),
    z.object({
        type: z.literal('score'),
        instructions: DecisionTextSchema,
        criteria: z.array(DecisionTextSchema).min(2).max(MAX_DECISION_SCORE_LEVELS)
    }),
    z.object({
        type: z.literal('boolean'),
        instructions: DecisionTextSchema,
        criteria: z.object({
            true: DecisionTextSchema.optional(),
            false: DecisionTextSchema.optional()
        }).optional()
    })
]);
const UnitIntervalSchema = z.number().min(0).max(1);
export const DecisionThresholdsSchema = z.object({
    auto: UnitIntervalSchema,
    propose: UnitIntervalSchema
}).refine((thresholds)=>thresholds.propose <= thresholds.auto, {
    message: 'propose must not exceed auto',
    path: [
        'propose'
    ]
});
/** Keyed by model name as a plain string: the config outlives model retirements. */ export const DecisionThresholdsByModelSchema = boundedRecord(IdentifierSchema, DecisionThresholdsSchema, 1, MAX_DECISION_THRESHOLD_MODELS, 'decisionThresholds');
/**
 * A prompt config's `decisionThresholds` block; `undefined` when the config
 * carries none. An invalid block is reported through `onInvalid`, not thrown:
 * the prompt itself stays usable and every answer bands `unknown`.
 */ export function parseDecisionThresholdsBlock(config, onInvalid) {
    const raw = typeof config === 'object' && config !== null ? config.decisionThresholds : undefined;
    if (raw === undefined) return undefined;
    const parsed = DecisionThresholdsByModelSchema.safeParse(raw);
    if (parsed.success) return parsed.data;
    onInvalid(parsed.error.message);
    return undefined;
}
/** What the SDK takes as evaluation state: text, a JSON array or a JSON object. */ export const DecisionStateSchema = z.union([
    z.string(),
    z.array(z.json()),
    z.record(z.string(), z.json())
]);
export const DecideRequestSchema = z.object({
    /** Keyed by question id — a lowercase slug (`[a-z0-9-]+`). */ questions: boundedRecord(SlugSchema, DecisionQuestionSchema, 1, MAX_DECISION_QUESTIONS, 'questions'),
    state: DecisionStateSchema,
    functionId: IdentifierSchema,
    modelSlot: SlugSchema.optional(),
    metadata: boundedRecord(IdentifierSchema, z.string().max(MAX_DECISION_METADATA_VALUE_LENGTH), 0, MAX_DECISION_METADATA_ENTRIES, 'metadata').optional(),
    /** By model name: only the host knows which model the call resolves to. */ thresholds: DecisionThresholdsByModelSchema.optional(),
    /** Echoed on the result only when `thresholds` covered the resolved model. */ thresholdsVersion: IdentifierSchema.optional(),
    langfusePrompt: IdentifierSchema.optional()
});

//# sourceMappingURL=ai-decision.js.map